/**
 * Hooks de escrita/leitura de Correção da Base (M8) sobre React Query.
 * Consomem a API REAL (EdicaoApi); erros genéricos (RN-GLOBAL-004) e nenhum dado
 * pessoal enviado a analytics/console (RN-M11-001).
 *
 * `useEditarVisita`/`useConferir` invalidam o cache da visita/histórico após a
 * escrita e propagam ConflictError (409) / ServiceUnavailableError (503) ao
 * chamador para tratamento na UI (ConflictBanner / toast).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import {
  ConferirResposta,
  EditarVisitaRequest,
  HistoricoEntry,
  VisitaAtualizada,
} from '../services/edicao-types';

/** GET /visitas/{id}/historico — histórico append-only da visita. */
export function useHistorico(id: string | null): {
  entradas: readonly HistoricoEntry[] | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { edicao } = useServices();
  const query = useQuery({
    queryKey: ['historico', id],
    queryFn: () => edicao.historico(id as string),
    enabled: id !== null,
  });
  return { entradas: query.data, carregando: query.isLoading, erro: query.isError };
}

/** PATCH /visitas/{id} — salva a edição e invalida a ficha/histórico. */
export function useEditarVisita(id: string): {
  editar: (req: EditarVisitaRequest) => Promise<VisitaAtualizada>;
  salvando: boolean;
} {
  const { edicao } = useServices();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (req: EditarVisitaRequest) => edicao.editar(id, req),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['visita', id] }),
        qc.invalidateQueries({ queryKey: ['historico', id] }),
      ]);
    },
  });
  return { editar: mutation.mutateAsync, salvando: mutation.isPending };
}

/** POST /visitas/{id}/conferir — marca/desmarca conferida. */
export function useConferir(id: string): {
  conferir: (rowVersion: number) => Promise<ConferirResposta>;
  salvando: boolean;
} {
  const { edicao } = useServices();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (rowVersion: number) => edicao.conferir(id, rowVersion),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['visita', id] }),
        qc.invalidateQueries({ queryKey: ['historico', id] }),
      ]);
    },
  });
  return { conferir: mutation.mutateAsync, salvando: mutation.isPending };
}
